import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Activity, Search, Loader2, Download, ChevronDown } from 'lucide-react';
import { format, subDays, differenceInDays } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { mockActivityLogs } from '@/data/mockData';
import { ActivitySummaryCards } from './activity-report/ActivitySummaryCards';
import { SummaryDetailSections } from './activity-report/SummaryDetailSections';
import { DateRangeFilter } from './activity-report/DateRangeFilter';
import { exportSummaryReport } from './activity-report/useExportActivityLogs';
import { useServiceCatalog } from '@/hooks/useServiceCatalog';

interface ActivityLog {
  id: string;
  user_id: string;
  action_type: string;
  action_details: any;
  ip_address: string | null;
  user_agent: string | null;
  site_id: string | null;
  created_at: string;
  user_name?: string;
  site_name?: string;
}

interface UserActivityLogsProps {
  isDemo?: boolean;
}

export const UserActivityLogs = ({ isDemo = false }: UserActivityLogsProps) => {
  const { isDemoMode } = useAuth();
  const { displayName: serviceDisplayName, catalog: serviceCatalog } = useServiceCatalog();
  const showDemoData = isDemo || isDemoMode;
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [allSites, setAllSites] = useState<{ id: string; name: string }[]>([]);
  const [filterSite, setFilterSite] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState<string>('all');
  const [startDate, setStartDate] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const d = params.get('start');
    return d ? new Date(d) : subDays(new Date(), 7);
  });
  const [endDate, setEndDate] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const d = params.get('end');
    return d ? new Date(d) : new Date();
  });
  const [logOpen, setLogOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const site = params.get('site');
    if (site) setFilterSite(site);
    supabase.from('sites').select('id, name').order('name').then(({ data }) => {
      if (data) setAllSites(data);
    });
  }, []);

  // Summary data state
  const [unbeddedClients, setUnbeddedClients] = useState<any[]>([]);
  const [facilityAlerts, setFacilityAlerts] = useState<any[]>([]);
  const [clientAlerts, setClientAlerts] = useState<any[]>([]);
  const [serviceBreakdown, setServiceBreakdown] = useState<Record<string, number>>({});
  const [overdueItems, setOverdueItems] = useState<any[]>([]);

  useEffect(() => {
    if (showDemoData) {
      setLogs(mockActivityLogs);
      setIsLoading(false);
    } else {
      loadAllData();
    }
  }, [showDemoData, startDate, endDate, serviceCatalog]);

  const loadAllData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        loadLogs(),
        loadUnbeddedClients(),
        loadAlerts(),
        loadServices(),
        loadOverdueTrainings(),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const { data: logsData, error } = await supabase
        .from('user_activity_logs')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;

      const userIds = [...new Set(logsData?.map(l => l.user_id) || [])];
      const siteIds = [...new Set(logsData?.filter(l => l.site_id).map(l => l.site_id) || [])];

      const { data: profiles } = await supabase.from('profiles').select('id, full_name').in('id', userIds);
      const { data: sites } = siteIds.length > 0
        ? await supabase.from('sites').select('id, name').in('id', siteIds.filter((id): id is string => id !== null))
        : { data: [] };

      setLogs(logsData?.map(log => ({
        ...log,
        user_name: profiles?.find(p => p.id === log.user_id)?.full_name || 'Unknown User',
        site_name: sites?.find(s => s.id === log.site_id)?.name || null,
      })) || []);
    } catch (error: any) {
      console.error('Error loading activity logs:', error);
      toast.error('Failed to load activity logs');
    }
  };

  const loadUnbeddedClients = async () => {
    try {
      const { data: clients } = await supabase
        .from('clients')
        .select('id, first_name, last_name, site_id, updated_at')
        .eq('status', 'unbedded')
        .is('archived_at', null);

      if (!clients?.length) { setUnbeddedClients([]); return; }

      const siteIds = [...new Set(clients.filter(c => c.site_id).map(c => c.site_id))];
      const { data: sites } = siteIds.length > 0
        ? await supabase.from('sites').select('id, name').in('id', siteIds.filter((id): id is string => id !== null))
        : { data: [] };

      setUnbeddedClients(clients.map(c => ({
        ...c,
        site_name: sites?.find(s => s.id === c.site_id)?.name || null,
      })));
    } catch (error) {
      console.error('Error loading unbedded clients:', error);
    }
  };

  const loadAlerts = async () => {
    try {
      const { data: alerts } = await supabase
        .from('alerts')
        .select('id, title, description, type, category, site_id, created_at')
        .eq('status', 'active');

      if (!alerts?.length) { setFacilityAlerts([]); setClientAlerts([]); return; }

      const siteIds = [...new Set(alerts.filter(a => a.site_id).map(a => a.site_id))];
      const { data: sites } = siteIds.length > 0
        ? await supabase.from('sites').select('id, name').in('id', siteIds.filter((id): id is string => id !== null))
        : { data: [] };

      const enriched = alerts.map(a => ({
        ...a,
        site_name: sites?.find(s => s.id === a.site_id)?.name || null,
      }));

      const facilityCategories = ['building', 'maintenance', 'facility', 'safety'];
      setFacilityAlerts(enriched.filter(a => facilityCategories.includes(a.category || '')));
      setClientAlerts(enriched.filter(a => !facilityCategories.includes(a.category || '')));
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const loadServices = async () => {
    try {
      const { data } = await supabase
        .from('service_logs')
        .select('service_type, quantity')
        .is('deleted_at', null)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      const breakdown: Record<string, number> = {};
      data?.forEach(s => {
        const key = serviceDisplayName(s.service_type);
        breakdown[key] = (breakdown[key] || 0) + (s.quantity || 1);
      });
      setServiceBreakdown(breakdown);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const loadOverdueTrainings = async () => {
    try {
      const [
        { data: assignments },
        { data: completions },
        { data: profiles },
        { data: materials },
      ] = await Promise.all([
        supabase.from('training_assignments').select('id, material_id, assigned_to_user_id, due_date').not('due_date', 'is', null),
        supabase.from('training_completions').select('material_id, user_id'),
        supabase.from('profiles').select('id, full_name'),
        supabase.from('training_materials').select('id, title').eq('is_active', true),
      ]);

      const today = new Date();
      const items: any[] = [];

      assignments?.forEach(a => {
        if (!a.due_date || !a.assigned_to_user_id) return;
        const dueDate = new Date(a.due_date);
        if (dueDate >= today) return;
        const isCompleted = completions?.some(c => c.material_id === a.material_id && c.user_id === a.assigned_to_user_id);
        if (isCompleted) return;

        items.push({
          staffName: profiles?.find(p => p.id === a.assigned_to_user_id)?.full_name || 'Unknown',
          trainingTitle: materials?.find(m => m.id === a.material_id)?.title || 'Unknown',
          dueDate: a.due_date,
          daysOverdue: differenceInDays(today, dueDate),
        });
      });

      setOverdueItems(items.sort((a, b) => b.daysOverdue - a.daysOverdue));
    } catch (error) {
      console.error('Error loading overdue trainings:', error);
    }
  };

  const totalServices = useMemo(() => Object.values(serviceBreakdown).reduce((s, v) => s + v, 0), [serviceBreakdown]);

  const formatActionType = (t: string) =>
    t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  // Patterns to filter out IDs and timestamps from the details display
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

  const formatActionDetails = (details: any): string => {
    if (!details) return '';
    if (typeof details !== 'object') return String(details);

    // Keys that hold IDs, timestamps, or other noise we don't want to show
    const skipKeys = new Set([
      'id', 'client_id', 'user_id', 'site_id', 'room_id', 'alert_id',
      'note_id', 'roster_id', 'inspection_id', 'document_id', 'staff_id',
      'mention_id', 'parent_note_id', 'partner_id', 'case_worker_id',
      'created_at', 'updated_at', 'timestamp', 'time', 'checked_in_at',
      'checked_out_at', 'acknowledged_at', 'reported_at', 'resolved_at'
    ]);

    const parts: string[] = [];
    for (const [key, value] of Object.entries(details)) {
      if (value == null || value === '') continue;
      if (skipKeys.has(key)) continue;
      const str = String(value);
      if (UUID_RE.test(str)) continue;
      if (ISO_DATE_RE.test(str)) continue;
      parts.push(str);
    }
    return parts.join(', ');
  };

  const getActionTextColor = (actionType: string) => {
    switch (actionType) {
      case 'login': return 'text-green-600 font-medium';
      case 'logout': return 'text-muted-foreground font-medium';
      case 'create_client':
      case 'update_client': return 'text-blue-600 font-medium';
      case 'create_alert':
      case 'acknowledge_alert': return 'text-orange-600 font-medium';
      case 'check_in': return 'text-green-600 font-medium';
      case 'check_out': return 'text-amber-600 font-medium';
      case 'reset_password':
      case 'manage_access': return 'text-red-600 font-medium';
      default: return 'text-muted-foreground font-medium';
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch =
      log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterAction === 'all' || log.action_type === filterAction;
    const matchesSite = filterSite === 'all' || log.site_id === filterSite;
    return matchesSearch && matchesFilter && matchesSite;
  });

  const actionTypes = [...new Set(logs.map(log => log.action_type))];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Summary Report</h2>
          <p className="text-sm text-muted-foreground">
            Operational overview of trainings, clients, services, and alerts
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => exportSummaryReport({ startDate, endDate, overdueItems, unbeddedClients, serviceBreakdown, facilityAlerts, clientAlerts, logs: filteredLogs })}>
          <Download className="h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Date range */}
      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onChangeRange={(s, e) => { setStartDate(s); setEndDate(e); }}
      />

      {/* Summary cards */}
      <ActivitySummaryCards
        overdueCount={overdueItems.length}
        unbeddedCount={unbeddedClients.length}
        totalServices={totalServices}
        facilityAlertCount={facilityAlerts.length}
        clientAlertCount={clientAlerts.length}
        totalActions={logs.length}
      />

      {/* Detail sections */}
      <SummaryDetailSections
        overdueItems={overdueItems}
        unbeddedClients={unbeddedClients}
        serviceBreakdown={serviceBreakdown}
        facilityAlerts={facilityAlerts}
        clientAlerts={clientAlerts}
      />

      {/* Collapsible Activity Log */}
      <Collapsible open={logOpen} onOpenChange={setLogOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Activity Log ({filteredLogs.length})
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${logOpen ? 'rotate-180' : ''}`} />
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <div className="flex gap-3 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by user or action..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterAction} onValueChange={setFilterAction}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {actionTypes.map(action => (
                      <SelectItem key={action} value={action}>
                        {formatActionType(action)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterSite} onValueChange={setFilterSite}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by facility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Facilities</SelectItem>
                    {allSites.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Facility</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No activity logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLogs.map(log => (
                      <TableRow key={log.id} className="h-10">
                        <TableCell className="py-2">
                          <span className="font-medium text-sm">{log.user_name}</span>
                        </TableCell>
                        <TableCell className="py-2">
                          <span className={`text-sm ${getActionTextColor(log.action_type)}`}>
                            {formatActionType(log.action_type)}
                          </span>
                        </TableCell>
                        <TableCell className="py-2 text-sm text-muted-foreground">
                          {log.site_name || '—'}
                        </TableCell>
                        <TableCell className="py-2 text-sm text-muted-foreground">
                          {format(new Date(log.created_at), 'MMM dd, HH:mm')}
                        </TableCell>
                        <TableCell className="py-2">
                          {(() => {
                            const formatted = formatActionDetails(log.action_details);
                            return formatted ? (
                              <span className="text-xs text-muted-foreground max-w-xs truncate block">
                                {formatted}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};
